import React, {useState, useMemo, useEffect, useCallback} from 'react';
import produce from 'immer';
import {useTranslation} from 'react-i18next';
import {SMCSelect, SMCOption} from '../../components/Simple/SMCSelect';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCButton from '../../components/Simple/SMCButton';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import EmptyRow from '../../components/Simple/EmptyRow';
import SMCModal from '../../components/core/SMCModal';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {PlanGetSchema} from '../../api/DBSchema/Plan';
import SMCNumberInput from '../../components/Simple/SMCNumberInput';
import {hasValue, replaceItemInIndex, isNumeric, setProperty} from '../../utils/utils';
import Sepperator from '../../components/Simple/Sepperator';
import RowLayout from '../../components/Layouts/RowLayout';
import SMCLabel from '../../components/Simple/SMCLabel';
import SMCLink from '../../components/Simple/SMCLink';
import Api from '../../api/SMPFetchHelper';
import {ObjectTypes} from '../../api/consts';
import {useSMCErrorHandler} from '../../utils/errorUtils';

const pageSchema = PlanGetSchema.pages.firmware;
const pageId = pageSchema.pageId;
const DAY_MILLIS = 86400000;

function GradualUpgradeModal({
    planObject,
    originalPlanObject,
    isOpen,
    onSuccess,
    onRequestClose
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    
    const [isFetchingPlan, setIsFetchingPlan] = useState(false);

    const [percentageArray, setPercentageArray] = useState([15,25,60,null, null]);
    const [numberOfStages, setNumberOfStages] = useState(3);
    const [upgradeDelay, setUpgradeDelay] = useState(5 * DAY_MILLIS);
    const [upgradeRunTime, setUpgradeRunTime] = useState(null);
    const [upgradeStartDate, setUpgradeStartDate] = useState(null);

    const inProgress = hasValue(upgradeStartDate);
    const isPaused   = !hasValue(upgradeStartDate) && hasValue(upgradeRunTime);

    const currentBulkIndex = useMemo(() => {
        let currentBulkIndex = null;
        if (inProgress || isPaused) {
            currentBulkIndex = 0;
            if (inProgress) {
                const now = new Date().getTime();
                let bulkEndTime = upgradeStartDate + upgradeDelay;
                while (now > bulkEndTime && currentBulkIndex < numberOfStages) {
                    currentBulkIndex++;
                    bulkEndTime += upgradeDelay;
                }
            } else {
                currentBulkIndex = Math.floor(upgradeRunTime / upgradeDelay);
            }
        }
        return currentBulkIndex;
    }, [inProgress, isPaused, numberOfStages, upgradeDelay, upgradeRunTime, upgradeStartDate]);

    const isFinished = currentBulkIndex >= numberOfStages;

    const getPercentageString = useCallback((doValidate = true) => {
        let sumOfPercentages = 0;
        let percentageString = '';
        for (let i = 0; i < numberOfStages; i++) {
            if (doValidate && !hasValue(percentageArray[i])) {
                alert(`Please enter a number in stage ${i+1} percentage`);
                throw new Error();
            }
            if (doValidate && !isNumeric(percentageArray[i])) {
                alert(`Please enter a legal number in stage ${i+1} percentage`);
                throw new Error();
            }
            const currentPercentage = Number(percentageArray[i]);
            sumOfPercentages += currentPercentage;
            if (i !== 0) {
                percentageString += ';';
            }
            percentageString += currentPercentage;
        }
        if (doValidate && sumOfPercentages > 100) {
            alert('Error: Total percentage can\'t be over 100 percent!');
            throw new Error();
        }
        if (doValidate && sumOfPercentages < 100) {
            if (!window.confirm('Total percentage under 100 percent, continue?')) {
                throw new Error();
            }
        }
        return percentageString;
    }, [numberOfStages, percentageArray]);

    const modifyPlan = useCallback(async (props) => {
        if (!props) {
            return;
        }
        const {percentageString, upgradeDelay, upgradeRunTime, upgradeStartDate} = props;
        const modifiedPlanObject = produce(originalPlanObject, draft => {
            (typeof percentageString !== 'undefined') && setProperty(draft, [pageId, pageSchema.gradualUpgradePercentage], percentageString);
            (typeof upgradeDelay  !== 'undefined') && setProperty(draft, [pageId, pageSchema.gradualUpgradeDelay], upgradeDelay);
            (typeof upgradeRunTime  !== 'undefined') && setProperty(draft, [pageId, pageSchema.gradualUpgradeRunTime], upgradeRunTime);
            (typeof upgradeStartDate  !== 'undefined') && setProperty(draft, [pageId, pageSchema.gradualUpgradeStartDate], upgradeStartDate);
        });

        setIsFetchingPlan(true);
        try {
            await Api.updateSingleObject(ObjectTypes.PLAN, originalPlanObject[PlanGetSchema.id], modifiedPlanObject);
            // const fetchResp = await Api.fetchSingleObject(ObjectTypes.PLAN, originalPlanObject[PlanGetSchema.id]);
            // const plan = fetchResp.data.data.plan;
            onSuccess(modifiedPlanObject[pageId]);
        } catch (e) {
            const errorMessage = getErrorMessage(e);
            alert(errorMessage);
        }
        setIsFetchingPlan(false);
    }, [originalPlanObject, onSuccess, getErrorMessage]);

    // Simulates skipping by moving the starting time forward by the days to skip
    const doSkipToIndex = useCallback((index) => {
        const skipToTime = index * upgradeDelay;
        const startDate = new Date().getTime() - skipToTime;
        modifyPlan({upgradeStartDate: startDate})
    }, [upgradeDelay, modifyPlan]);

    const handleStart = useCallback(() => {
        let startDate = new Date().getTime();
        if (upgradeRunTime) {
            startDate -= upgradeRunTime;
        }
        let percentageStringIsLegal = true;
        let percentageString;
        // If paused, don't set percentage string
        if (!isPaused) {
            try {
                percentageString = getPercentageString(true);
            } catch (e) {
                percentageStringIsLegal = false;
            }
        }
            
        if (percentageStringIsLegal) {
            modifyPlan({
                percentageString: percentageString,
                upgradeDelay: upgradeDelay,
                upgradeRunTime: upgradeRunTime,
                upgradeStartDate: startDate,
                
            });
        }
    }, [upgradeRunTime, modifyPlan, getPercentageString, upgradeDelay, isPaused]);

    const handleStop = useCallback(() => {
        modifyPlan({
            upgradeRunTime: null,
            upgradeStartDate: null,
            
        });
    }, [modifyPlan]);

    const handlePause = useCallback(() => {
        const runTime = new Date().getTime() - upgradeStartDate;
        modifyPlan({
            upgradeStartDate: null,
            upgradeRunTime: runTime
        });
    }, [upgradeStartDate, modifyPlan]);

    const handleSave = async () => {
        let percentageStringIsLegal = true;
        let percentageString;
        // If in progress or paused, don't set percentage
        if (!inProgress && !isPaused) {
            try {
                percentageString = getPercentageString(true);
            } catch (e) {
                percentageStringIsLegal = false;
            }
        }
        
        if (percentageStringIsLegal) {
            modifyPlan({
                percentageString: percentageString,
                upgradeDelay: upgradeDelay,
                upgradeRunTime: upgradeRunTime,
                upgradeStartDate: upgradeStartDate
            });
        }
    }

    // On each new open, or change of originalPlanObject, set values
    useEffect(() => {
        if (isOpen) {
            const firmwarePage = planObject[pageId];
            const percentageString = firmwarePage[pageSchema.gradualUpgradePercentage];
            let percentageArray;
            if (hasValue(percentageString)) {
                percentageArray = percentageString
                        .split(';')
                        .map(num => {
                            if (!num || isNaN(Number(num))) {
                                return null;
                            }
                            return Number(num)
                        });
            }
            else {
                percentageArray = [15,25,60];
            }
            const percentageArrayLength = percentageArray.length;
            setNumberOfStages(percentageArrayLength);
            percentageArray.length = 5;
            percentageArray.fill(null, percentageArrayLength, 5);
            setPercentageArray(percentageArray);
            setUpgradeDelay(firmwarePage[pageSchema.gradualUpgradeDelay]);
            setUpgradeStartDate(firmwarePage[pageSchema.gradualUpgradeStartDate]);
            setUpgradeRunTime(firmwarePage[pageSchema.gradualUpgradeRunTime]);
        }
    }, [isOpen, planObject]);

    return (
        <SMCModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={520}
            height={550}
            title={t('SMP.PLAN.SERVICES.FIRMWARE.GRADUAL_UPGRADE_CONTROL')}
            loading={isFetchingPlan}
        >
            <SMCModalBody labelWidth={170}>
                {/* Number of stages */}
                <SMCSelect
                    value={numberOfStages}
                    onChange={e => setNumberOfStages(parseInt(e.target.value))}
                    label={t('SMC_GRADUAL_NUM_OF_STAGES')+':'}
                    disabled={inProgress || isPaused}
                >
                    {
                        [...Array(5)].map((e, i) => (
                            <SMCOption value={i+1} key={i+1}>{i+1}</SMCOption>
                        ))
                    }
                </SMCSelect>

                <EmptyRow />
                
                {/* Stage percentages */}
                {
                    [...Array(numberOfStages)].map((e, index) => (
                        <RowLayout key={index}>
                            <SMCNumberInput
                                noNegative={true}
                                value={percentageArray[index]}
                                onChange={e => setPercentageArray(replaceItemInIndex(percentageArray, parseInt(e.target.value), index))}
                                disabled={inProgress || isPaused}
                                label={<SMCLabel bold={currentBulkIndex === index}>{t('SMC_GRADUAL_STAGE')} {index+1} {t('SMC_GRADUAL_PERCENTAGE')}:</SMCLabel>}
                                style={{ fontWeight: currentBulkIndex === index ? 'bold' : undefined }}
                            />
                            {
                                inProgress && currentBulkIndex < index && (
                                    <SMCLink underline onClick={() => doSkipToIndex(index)}>{t('SMC_GRADUAL_SKIP_TO_HERE')}</SMCLink>
                                )
                            }

                        </RowLayout>
                    ))
                }
                <Sepperator />
                <RowLayout>
                    <SMCLabel useContextWidth tooltip={'Each day is configured as 24 hours from the moment you press \'Start\''}>{t('SMC_GRADUAL_DELAY_BETWEEN_STAGES')+':'}</SMCLabel>
                    <SMCSelect
                        value={upgradeDelay}
                        onChange={e => setUpgradeDelay(parseInt(e.target.value))}
                        width={45}
                        style={{ marginRight: '8px' }}
                        disabled={inProgress || isPaused}
                    >
                        {
                            [...Array(60)].map((e, i) => (
                                <SMCOption value={(i+1)*DAY_MILLIS} key={i}>{i+1}</SMCOption>
                            ))
                        }
                    </SMCSelect>
                    <SMCLabel>{t('SMC_GRADUAL_DELAY_BETWEEN_STAGES_DAYS')}</SMCLabel>
                </RowLayout>

                <EmptyRow />

                {
                    isPaused && (
                        <SMCLabel bold>{t('SMC_GRADUAL_PAUSED')}</SMCLabel>
                        )
                    }
                {
                    isFinished && (
                        <SMCLabel bold>{t('SMC_GRADUAL_FINISHED_GRADUAL_UPGRADE')}</SMCLabel>
                    )
                }
                
            </SMCModalBody>
            <SMCModalButtonContainer
                right={
                    <>
                    <SMCButton onClick={handleSave}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                        {t('SMC_GEN_SAVE')}
                    </SMCButton>
                    <SMCButton onClick={onRequestClose}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                        {t('SMC_GEN_CLOSE')}
                    </SMCButton>
                    </>
                }
                left={
                    <>
                    <SMCButton onClick={handleStart} disabled={inProgress && !isPaused}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="play" />
                        {t('SMC_GEN_START')}
                    </SMCButton>
                    <SMCButton onClick={handleStop} disabled={!inProgress && !isPaused}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="stop" />
                        {t('SMC_GEN_STOP')}
                    </SMCButton>
                    <SMCButton onClick={handlePause} disabled={!inProgress || isPaused}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="pause" />
                        {t('SMC_GEN_PAUSE')}
                    </SMCButton>
                    </>
                }
            />
        </SMCModal>
    );
}

export default GradualUpgradeModal;